import { Container, Grid, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useContext, useEffect, useState } from 'react';
import { AgentNetDivider } from 'ui-kit/components/dividers/AgentNetDivider2';
import { UnderwritingRequest, UnderwritingRequestInformation } from './types';

import { pxToRem } from '@fluentsms/agentnet-web-components';
import { HelpOutline } from '@material-ui/icons';
import clsx from 'clsx';
import { format as formatDate } from 'date-fns';
import { KBLinks } from 'features/constants';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import moment from 'moment';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import RadioGroup, { RadioProps } from 'ui-kit/components/radios/RadioGroup';
import { AgentNetTextInput, CurrencyField } from 'ui-kit/inputs';
import AgentNetDropdownSelector, { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { RequestInformationSchema } from 'utilities/validation/schemas/underwriting-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { HiLiDecisionStatus, RequestStatus, underwritingText } from './constants';

interface UnderwritingProps {
  uwr?: UnderwritingRequest;
  updateUnderwriting: any;
  estimatedDate: string | null;
  setEstimatedDate: (value: string | null) => void;
  submitAttempted: boolean;
  setSubmitAttempted: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmit: boolean;
  updateUwrErrorStatus: any;
  openDocumentDrawer: any;
  openReopenConformation: any;
  isUwrDisabled?: boolean;
  isDisabled?: boolean;
}

const UnderwritingRequestInformationFC: React.FC<UnderwritingProps> = ({
  uwr,
  updateUnderwriting,
  estimatedDate,
  setEstimatedDate,
  submitAttempted,
  setSubmitAttempted,
  updateUwrErrorStatus,
  openDocumentDrawer,
  openReopenConformation,
  isUwrDisabled,
  isDisabled,
}: UnderwritingProps) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        background: theme.palette.common.white,
      },
      UnderwritingWrap: {
        padding: pxToRem(0, 24, 88),
      },
      mb2: {
        marginBottom: theme.spacing(2),
      },
      ml12: {
        marginLeft: '12px',
      },
      fieldPadding: {
        marginTop: theme.spacing(2),
      },
      fieldPadding2: {
        marginTop: theme.spacing(2.5),
      },
      twoCol: {
        display: 'flex',
      },
      warningMsg: { marginBottom: theme.spacing(3) },
      dropDownWithToolTip: {
        display: `flex`,
      },
      subTitle: {
        display: 'inline',
        fontWeight: 700,
      },
    }),
  );

  const iconStyles = { marginLeft: pxToRem(12), marginTop: '6px' };

  const mortgageModifiedOptions: RadioProps[] = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];

  const multisiteOptions: RadioProps[] = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];

  // Adding state variables
  const [transactionTypeOptions, setTransactionTypeOptions] = useState<SelectOption[]>([]);
  const [approverOptions, setapproverOptions] = useState<SelectOption[]>([]);

  // Form Validation State Vars
  const [showMandatoryField, setShowMandatoryField] = useState(false);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);

  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { fileData } = fileDataCtx;

  const isFloridaState = fileData?.propertyState === 'FL';

  const validateErrors = async (uwrInfo: UnderwritingRequestInformation) => {
    const errs: FieldValidationError[] = (await doValidate(uwrInfo, RequestInformationSchema)) || [];

    setValidationErrors(errs);
    updateUwrErrorStatus('RequestInformation', errs?.length === 0);
  };

  useEffect(() => {
    const transactionTypeOptionsMap =
      uwr?.TemplateMetaData.TransactionTypes.map((x, i) => {
        return { name: x.Name, value: x.Name };
      }) ?? [];

    const approverOptionsMap =
      uwr?.TemplateMetaData.Approvers.map((x, i) => {
        return { name: x.Name, value: x.Name };
      }) ?? [];

    setTransactionTypeOptions(transactionTypeOptionsMap);
    setapproverOptions(approverOptionsMap);
  }, [uwr?.TemplateMetaData]);

  useEffect(() => {
    validateErrors(uwr?.RequestInformation || {});
  }, [uwr?.RequestInformation]);

  useEffect(() => {
    if (submitAttempted) {
      setShowMandatoryField(true);
      setSubmitAttempted(false);
    }
  }, [submitAttempted]);

  const classes = useStyles();

  const getFormattedDate = (input: string | Date | undefined) => {
    if (!input) {
      return '';
    }

    const padded = (num: number) => {
      return num < 10 ? '0' + num : '' + num;
    };

    try {
      const date = new Date(input);

      return `${padded(date.getMonth() + 1)}/${date.getDate()}/${date.getFullYear()}`;
    } catch {
      return '';
    }
  };

  const updateUnderwritingReqInfo = (field: string, value: any) => {
    const reqInfo = { ...uwr?.RequestInformation, [field]: value };
    updateUnderwriting('RequestInformation', reqInfo);
  };

  const assignedToDisable = () => {
    if (isUwrDisabled) {
      return true;
    } else if (uwr?.RequestInformation.HiLiDecisionStatus === HiLiDecisionStatus.submit) {
      return true;
    } else if (uwr?.RequestInformation.RequestStatus === RequestStatus.submitted) {
      return true;
    } else if (
      uwr?.RequestInformation.RequestStatus === RequestStatus.inProgress &&
      uwr?.RequestInformation.HiLiDecisionStatus === undefined
    ) {
      return true;
    } else {
      return isDisabled;
    }
  };

  useEffect(() => {
    let time = '';
    if (estimatedDate) {
      time = new Date(estimatedDate !== null ? estimatedDate : '').toLocaleString();
    }
    time = moment(time).format('YYYY-MM-DD');
    updateUnderwritingReqInfo('EstimatedClosingDate', time);
  }, [estimatedDate]);

  return (
    <Container>
      {!isDisabled && (
        <PageHeader
          title="Underwriting Request"
          subtitle={
            <>
              Submit requests for underwriting authorization, including high liability transactions
              {isFloridaState ? ' and ' : ', '}
              Closing Protection Letter (CPL) modifications
              {isFloridaState ? '. ' : ', and other specialized underwriting reviews. '}
              <Typography className={classes.subTitle}>
                <em>
                  {isFloridaState ? (
                    <>
                      For general underwriting questions, please click to email the Florida Underwriting team at{' '}
                      <a href="mailto:floridauw@firstam.com">floridauw@firstam.com</a>.
                    </>
                  ) : (
                    <>For general underwriting questions, please contact your local underwriter directly.</>
                  )}
                </em>
              </Typography>
            </>
          }
          menuItems={[
            {
              label: 'Knowledge Base',
              link: KBLinks.underwritingRequest,
            },
          ]}
          disableMargin="x"
          divider={true}
        />
      )}
      <Grid container>
        {isDisabled && (
          <Grid item sm={12}>
            <AgentNetDivider
              typoVariant="h2"
              title={'Underwriting Request Information'}
              disablePaddingX
              primaryButtonName="Reopen Request"
              onClickPrimary={(e: any) => {
                openReopenConformation();
              }}
              primaryButtonDisable={isUwrDisabled}
              buttonName="Add Document(s)"
              onClick={(e: any) => {
                openDocumentDrawer();
              }}
              disableButton={isUwrDisabled}
            />
          </Grid>
        )}
      </Grid>

      {(uwr?.RequestInformation?.RequestId ?? 0) != 0 && (
        <Grid container spacing={3}>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              variant="standard"
              label="Request ID"
              name="RequestId"
              data-testid={'UnderwritingRequestId'}
              value={uwr?.RequestInformation.RequestId ?? ''}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 18 },
              }}
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              variant="standard"
              label="Submitted Date"
              name="SubmittedDate"
              data-testid={'UnderwritingSubmittedDate'}
              value={getFormattedDate(uwr?.RequestInformation.SubmittedDate)}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 18 },
              }}
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              variant="standard"
              label="Request Status"
              name="RequestStatus"
              data-testid={'UnderwritingRequestStatus'}
              value={uwr?.RequestInformation.RequestStatus ?? ''}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 18 },
              }}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item sm={3} className={classes.fieldPadding}>
          <AgentNetTextInput
            fullWidth
            variant="outlined"
            label="Request Name"
            name="RequestName"
            disabled={isUwrDisabled || isDisabled}
            data-testid={'UnderwritingRequestName'}
            value={uwr?.RequestInformation.RequestName ?? ''}
            required={true}
            onChange={(event: any) => {
              updateUnderwritingReqInfo('RequestName', event.target.value);
            }}
            showValidation={showMandatoryField}
            errs={validationErrors}
          />
        </Grid>
        <Grid item sm={3} className={classes.fieldPadding}>
          <DateFieldString
            fullWidth
            variant="outlined"
            label="Estimated Closing Date"
            name="EstimatedClosingDate"
            disabled={isUwrDisabled || isDisabled}
            data-testid={'UnderwritingEstimatedClosingDate'}
            value={(() => {
              const dateParsed = Date.parse(estimatedDate || '');
              return dateParsed ? formatDate(dateParsed, 'MM/dd/yyyy') : estimatedDate;
            })()}
            required={true}
            onChange={(val: any) => {
              setEstimatedDate(val !== '' ? val : null);
            }}
            showValidation={showMandatoryField}
            errs={validationErrors}
          />
        </Grid>
        <Grid item sm={3} className={classes.fieldPadding} style={{ display: 'flex' }}>
          <Grid item sm={12}>
            <CurrencyField
              fullWidth
              variant="outlined"
              label="Maximum Liability Amount"
              name="MaximumLiabilityAmount"
              id="MaximumLiabilityAmount"
              data-testid={'UnderwritingMaximumLiabilityAmount'}
              value={uwr?.RequestInformation.MaximumLiabilityAmount}
              max={100000000000}
              allowNegative={false}
              min={0}
              required={true}
              onChange={(event: any) => {
                updateUnderwritingReqInfo('MaximumLiabilityAmount', event.target.value);
              }}
              disabled={isUwrDisabled || isDisabled}
              showValidationOnBlur={showMandatoryField}
              alwaysDisplayError={showMandatoryField}
              errs={validationErrors}
            />
          </Grid>
          <Tooltip
            data-testid={'maxLiabililityToolTip'}
            title={underwritingText.MAX_LIABILITY_TOOLTIP_MSG}
            style={iconStyles}
          >
            <HelpOutline color="primary" />
          </Tooltip>
        </Grid>
        <Grid item sm={3} className={classes.fieldPadding}>
          <AgentNetDropdownSelector
            label={'Transaction Type'}
            name={'TransactionType'}
            qaAttribute={'UnderwritingTransactionType'}
            options={transactionTypeOptions}
            value={uwr?.RequestInformation.TransactionType}
            menuOption={(val: string, name: any) => {
              updateUnderwritingReqInfo('TransactionType', val);
            }}
            disabled={isUwrDisabled || isDisabled}
            dropdowntype="outlined"
          />
        </Grid>
      </Grid>

      {uwr?.RequestInformation.TransactionType === 'Other Financing' && (
        <Grid container spacing={3} className={classes.fieldPadding}>
          <Grid item sm={6}>
            <AgentNetTextInput
              fullWidth
              variant="outlined"
              label="Describe Transaction Type"
              name="TransactionTypeDescription"
              data-testid={'UnderwritingTransactionTypeDescription'}
              value={uwr?.RequestInformation.TransactionTypeDescription ?? ''}
              disabled={isUwrDisabled || isDisabled}
              required
              showValidation={showMandatoryField}
              errs={validationErrors}
              onChange={(event: any) => {
                updateUnderwritingReqInfo('TransactionTypeDescription', event.target.value);
              }}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item sm={3} className={clsx('dropDownWithToolTip', classes.dropDownWithToolTip, classes.fieldPadding2)}>
          <AgentNetDropdownSelector
            label={'Assign To'}
            name={'AssignedTo'}
            qaAttribute={'UnderwritingAssignedTo'}
            options={approverOptions}
            value={uwr?.RequestInformation.AssignedTo ?? approverOptions[0]?.name}
            disabled={assignedToDisable()}
            required
            menuOption={(val: string, name: any) => {
              updateUnderwritingReqInfo('AssignedTo', val);
            }}
            dropdowntype="outlined"
            showValidation={showMandatoryField}
            errs={validationErrors}
          />
          <Tooltip data-testid={'assignToToolTip'} title={underwritingText.ASSIGN_TO_TOOLTIP_MSG} style={iconStyles}>
            <HelpOutline color="primary" />
          </Tooltip>
        </Grid>
        <Grid item sm={3} className={classes.fieldPadding2}>
          <AgentNetTextInput
            fullWidth
            variant="outlined"
            label="Email Notification"
            name="EmailNotification"
            data-testid={'UnderwritingEmailNotification'}
            value={uwr?.RequestInformation.EmailNotification ?? ''}
            onChange={(event: any) => {
              updateUnderwritingReqInfo('EmailNotification', event.target.value);
            }}
            disabled={isUwrDisabled || isDisabled}
          />
        </Grid>
        <Grid item sm={6} className={classes.twoCol}>
          <Grid item sm={6}>
            <RadioGroup
              options={mortgageModifiedOptions}
              onChange={(el: React.ChangeEvent<HTMLInputElement>) => {
                updateUnderwritingReqInfo('IsMortgageModified', el.target.value === 'true');
              }}
              label={'Mortgage Modification?'}
              selected={mortgageModifiedOptions.find((i) => {
                return i.value == `${uwr?.RequestInformation.IsMortgageModified}`;
              })}
              compact
              disabled={isUwrDisabled || isDisabled}
            />
          </Grid>
          <Grid item sm={5} className={classes.ml12}>
            <RadioGroup
              options={multisiteOptions}
              onChange={(el: React.ChangeEvent<HTMLInputElement>) => {
                updateUnderwritingReqInfo('IsMultisite', el.target.value === 'true');
              }}
              label={'Multisite?'}
              selected={multisiteOptions.find((i) => {
                return i.value == `${uwr?.RequestInformation.IsMultisite}`;
              })}
              compact
              disabled={isUwrDisabled || isDisabled}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid container className={classes.fieldPadding}>
        <AgentNetTextInput
          fullWidth
          multiline
          variant="outlined"
          label="Description"
          name="Description"
          data-testid={'UnderwritingDescription'}
          value={uwr?.RequestInformation.Description ?? ''}
          onChange={(event: any) => {
            updateUnderwritingReqInfo('Description', event.target.value);
          }}
          disabled={isUwrDisabled || isDisabled}
        />
      </Grid>
    </Container>
  );
};

export default UnderwritingRequestInformationFC;
